import { throttle } from "lodash";
import "mapbox-gl/dist/mapbox-gl.css";

import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactMapGL, { FullscreenControl, Marker, NavigationControl, Popup } from "react-map-gl";
import SwipeableViews from "react-swipeable-views";
import { bindKeyboard, virtualize } from "react-swipeable-views-utils";

import { ApiHotelItem } from "@/api/hotel";
import useEvent from "@/hooks/useEvent";
import { useListenEvent } from "@/hooks/useEventEmitter";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";

import { CardInfo, Info } from "./Info";
import Pin from "./Pin";
import { MockDataCites } from "./data";
import { calcBestBounds, calcCenterPoint } from "./utils";

const VirtualizeSwipeableViews = bindKeyboard(virtualize(SwipeableViews));

const TOKEN =
  "pk.eyJ1IjoidHlsZXJhd2FyZHRvb2wiLCJhIjoiY2x1aGJhYzhmMjVlOTJqa2k2bWR5cW9hdyJ9.n4s9mtPLTLA7xdeQLl-deA";

const HotelMapBox = (props: { data: ApiHotelItem[] }) => {
  const { data } = props;
  // const data = MockDataCites;
  const { isPc, isMobile } = usePlatform();
  const [popupInfo, setPopupInfo] = useState<ApiHotelItem | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoverIndex, setHoverIndex] = useState(-1);

  const [viewState, setViewState] = useState<{
    longitude: number;
    latitude: number;
    zoom: number;
  }>();

  // console.log("%c [  ]-39", "font-size:13px; background:pink; color:#bf2c9f;", props.data);

  useLayoutEffect(() => {
    requestAnimationFrame(() => {
      const mapWidth = document.querySelector(".mapboxgl-canvas")?.clientWidth;
      const mapHeight = document.querySelector(".mapboxgl-canvas")?.clientHeight;

      if (mapWidth && mapHeight) {
        const viewportPoint = calcCenterPoint(data.map((item) => item.hotel_location));
        const zoom = calcBestBounds(
          data.map((item) => item.hotel_location),
          mapWidth,
          mapHeight
        );

        if (viewportPoint && zoom) {
          console.log("%c [  ]-54", "font-size:13px; background:pink; color:#bf2c9f;", {
            ...viewportPoint,
            zoom: Math.max(1, zoom),
          });
          setViewState({ ...viewportPoint, zoom: Math.max(1, zoom) });
        }
      }
    });
  }, [data]);

  const theme = useThemeData();

  useListenEvent("hotel-maker-light", (data: number) => {
    setHoverIndex(data);
  });

  const timer = useRef<NodeJS.Timeout | null>(null);

  const handleClose = useEvent(() => {
    timer.current = setTimeout(() => {
      console.log("onMouseLevel");
      setPopupInfo(null);
      setHoverIndex(-1);
    }, 500);
  });

  const pins = useMemo(
    () =>
      data.map((item, idx) => (
        <Marker
          key={`marker-${idx}`}
          longitude={item.hotel_location.longitude}
          latitude={item.hotel_location.latitude}
          anchor="bottom"
          style={{ zIndex: (activeIndex || hoverIndex) === idx ? 2 : 1 }}
        >
          <div
            onMouseEnter={() => {
              console.log("onMouseEnter");
              if (timer.current) {
                clearTimeout(timer.current);
              }
              setPopupInfo(item);
              setHoverIndex(idx);
            }}
            onMouseLeave={(event: React.MouseEvent<HTMLDivElement>) => {
              if (!event.relatedTarget || !event.relatedTarget?.closest?.("#simple-popper")) {
                handleClose();
              }
            }}
            onClick={() => {
              if (isMobile) {
                setActiveIndex(idx);
              }
            }}
          >
            <Pin active={(activeIndex || hoverIndex) === idx} data={item} />
          </div>
        </Marker>
      )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeIndex, data, hoverIndex]
  );

  const handleMove = useMemo(() => {
    return throttle(setViewState, 100);
  }, []);

  const renderSlider = useEvent(({ key, index }: { key: number; index: number }) => {
    return <CardInfo key={key} data={data[index]} />;
  });

  return (
    <div className="h-[100%] w-full">
      <ReactMapGL
        {...viewState}
        // initialViewState={{
        //   longitude: -98.31222,
        //   latitude: 39.17772,
        //   zoom: 4,
        //   bearing: 0,
        //   pitch: 30,
        // }}
        mapStyle={
          theme.isDarkMode ? "mapbox://styles/mapbox/dark-v9" : "mapbox://styles/mapbox/streets-v9"
        }
        mapboxAccessToken={TOKEN}
        onMove={handleMove}
      >
        <FullscreenControl position="top-left" />
        <NavigationControl position="top-left" />

        {pins}

        {isPc && popupInfo && (
          <Popup
            anchor="top"
            longitude={Number(popupInfo.hotel_location.longitude)}
            latitude={Number(popupInfo.hotel_location.latitude)}
            onClose={() => setPopupInfo(null)}
            closeButton={false}
            className="z-10"
          >
            <Info
              aria-describedby="#simple-popper"
              onMouseEnter={() => {
                if (timer.current) {
                  clearTimeout(timer.current);
                }
              }}
              onMouseLeave={handleClose}
              data={popupInfo}
            />
          </Popup>
        )}
      </ReactMapGL>

      {isMobile && (
        <div className="fixed bottom-[10px] left-0 w-[100%] z-[3]">
          <VirtualizeSwipeableViews
            index={activeIndex}
            onChangeIndex={setActiveIndex}
            style={{ padding: "0 35px" }}
            slideStyle={{ padding: "0 5px" }}
            slideRenderer={renderSlider}
            slideCount={data.length}
          />
        </div>
      )}
    </div>
  );
};

export default memo(HotelMapBox);
